import { useEffect, useRef, useState } from "react";
import { household, type Household } from "../api/client";
import { clearUserCaches } from "../lib/auth";

/** Topbar pill that names the active household and (when there's more than
 *  one) opens a dropdown to switch between them. The component is its own
 *  data source so it can refresh after a switch — pages just consume the
 *  effects via a full reload to /. */
export default function HouseholdSwitcher({ compact = false }: { compact?: boolean }) {
  const [list, setList] = useState<Household[] | null>(null);
  const [active, setActive] = useState<Household | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    household
      .list()
      .then((rows) => {
        setList(rows);
        // The backend marks the active household via /me — but here we read
        // it from /household which doesn't include is_active. So we ask the
        // backend which one is active via the /api/household endpoint.
        return household.get();
      })
      .then(setActive)
      .catch(() => {
        setList([]);
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  async function switchTo(id: number) {
    if (busy || !active || id === active.id) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await household.switch(id);
      // Full reload back to /: every page is household-scoped. The persisted
      // caches (identity + data) MUST go first, otherwise the boot after the
      // reload would flash the previous household's numbers from cache.
      clearUserCaches();
      window.location.assign("/");
    } catch {
      setBusy(false);
      setOpen(false);
    }
  }

  if (!active || !list || list.length === 0) return null;

  const label = active.name;
  // Pill is always tappable — even with one household, opening the dropdown
  // exposes "+ Neuer Haushalt" so the multi-household feature is at least
  // discoverable for solo users.
  const pillClasses =
    "inline-flex items-center gap-1.5 rounded-full border border-ink-800 bg-ink-900/70 hover:bg-ink-800 transition px-2.5 py-1 text-xs font-medium text-ink-100 max-w-[180px] cursor-pointer" +
    (compact ? "" : " md:max-w-none");

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={pillClasses}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Aktiv: ${label} — klicken für Haushalts-Menü`}
      >
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-400" />
        <span className="truncate">{label}</span>
        <span className="opacity-60 text-[10px]">▾</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-1.5 min-w-[200px] rounded-lg border border-ink-800 bg-ink-950 shadow-xl py-1 z-50"
        >
          {list.map((hh) => {
            const isActive = hh.id === active.id;
            return (
              <button
                key={hh.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => switchTo(hh.id)}
                disabled={busy}
                className={
                  "w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition " +
                  (isActive
                    ? "bg-ink-800/60 text-ink-100"
                    : "text-ink-300 hover:bg-ink-800 hover:text-ink-100")
                }
              >
                <span
                  aria-hidden
                  className={
                    "h-1.5 w-1.5 rounded-full " +
                    (isActive ? "bg-brand-400" : "bg-ink-600")
                  }
                />
                <span className="flex-1 truncate">{hh.name}</span>
                {isActive && <span className="text-[10px] opacity-60">aktiv</span>}
              </button>
            );
          })}
          <div className="border-t border-ink-800 mt-1">
            <a
              href="/einstellungen"
              className="block px-3 py-2 text-xs text-ink-400 hover:text-ink-100 hover:bg-ink-800"
            >
              + Neuer Haushalt
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
